import type { ChessColor, ChessMoveRecord, PieceType } from '@bn/shared';

export interface SquarePiece {
  type: PieceType;
  color: ChessColor;
}

/** Glyphes Unicode pleins : la couleur est portée par le style, pas par le caractère. */
export const PIECE_GLYPH: Record<PieceType, string> = {
  k: '♚',
  q: '♛',
  r: '♜',
  b: '♝',
  n: '♞',
  p: '♟',
};

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
export const RANKS = [8, 7, 6, 5, 4, 3, 2, 1];

/** Nom d'une case à partir de ses indices d'affichage (ligne 0 = 8ᵉ rangée). */
export function squareName(row: number, col: number): string {
  return `${FILES[col] ?? '?'}${RANKS[row] ?? '?'}`;
}

/**
 * Décompose la partie « placement » d'une notation FEN en 64 cases,
 * de a8 (index 0) à h1 (index 63).
 */
export function parseFen(fen: string): Array<SquarePiece | null> {
  const squares: Array<SquarePiece | null> = new Array(64).fill(null);
  const placement = fen.split(' ')[0] ?? '';
  let index = 0;

  for (const char of placement) {
    if (char === '/') continue;
    if (char >= '1' && char <= '8') {
      index += Number(char);
      continue;
    }
    if (index >= 64) break;
    squares[index] = {
      type: char.toLowerCase() as PieceType,
      color: char === char.toUpperCase() ? 'w' : 'b',
    };
    index += 1;
  }

  return squares;
}

/** Case du roi du camp demandé, pour signaler l'échec. */
export function findKing(
  squares: Array<SquarePiece | null>,
  color: ChessColor,
): string | null {
  for (let index = 0; index < squares.length; index += 1) {
    const piece = squares[index];
    if (piece?.type === 'k' && piece.color === color) {
      return squareName(Math.floor(index / 8), index % 8);
    }
  }
  return null;
}

export interface MovePair {
  number: number;
  white?: ChessMoveRecord;
  black?: ChessMoveRecord;
}

/** Regroupe l'historique en coups numérotés, comme sur une feuille de partie. */
export function toMovePairs(history: ChessMoveRecord[]): MovePair[] {
  const pairs: MovePair[] = [];
  for (const record of history) {
    const last = pairs[pairs.length - 1];
    if (record.color === 'w' || !last || last.black) {
      pairs.push(
        record.color === 'w'
          ? { number: pairs.length + 1, white: record }
          : { number: pairs.length + 1, black: record },
      );
    } else {
      last.black = record;
    }
  }
  return pairs;
}

/** Valeur matérielle classique, pour afficher l'avantage en pions. */
const MATERIAL: Record<PieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

export function materialBalance(captured: Record<ChessColor, PieceType[]>): number {
  const lostByBlack = captured.b.reduce((sum, piece) => sum + MATERIAL[piece], 0);
  const lostByWhite = captured.w.reduce((sum, piece) => sum + MATERIAL[piece], 0);
  return lostByBlack - lostByWhite;
}
